import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useTransferCustomization } from '@wetransfer/transfer/component/TransferCustomizationProvider/hooks';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import { usePrevious } from '@wetransfer/transfer/hooks/usePrevious';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { selectTransferState } from '@wetransfer/transfer/store/selector/transfer';
import { selectIsUnpaidUser } from '@wetransfer/transfer/store/selector/user';
import { TRANSFER_STATES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-states';

import {
  CLOSE_POPOVER_COOKIE_EXPIRY,
  MAX_POPOVER_VIEWS,
  MIN_POPOVER_VIEWS,
} from './constants';
import { getFutureDate } from './utils';

export const useCustomWallpaperPopover = (): {
  isShowPopover: boolean;
  onViewPopover: () => void;
  onDismissPopover: (isForever?: boolean) => void;
  onAcceptPopover: () => void;
} => {
  const { isEnabledForSender } = useTransferCustomization();

  // Show the popover
  const isUnpaidUser = useSelector(selectIsUnpaidUser);
  const transferState = useSelector(selectTransferState);
  const prevTransferState = usePrevious(transferState);
  const [isShowPopover, setIsShowPopover] = useState(false);
  useEffect(() => {
    if (
      !isEnabledForSender ||
      isUnpaidUser ||
      prevTransferState === transferState ||
      transferState !== TRANSFER_STATES.INIT
    ) {
      return;
    }

    const isDismissedForever = Boolean(
      Cookie.get(AppConfig.cookies.customWallpaperPopoverDismissedForever.name)
    );

    const viewCount = Number(
      Cookie.get(AppConfig.cookies.customWallpaperPopoverSeenCount.name) || 0
    );

    const wasClosedRecently = Cookie.get(
      AppConfig.cookies.customWallpaperPopoverClosed.name
    );

    if (
      !isDismissedForever &&
      !wasClosedRecently &&
      viewCount < MAX_POPOVER_VIEWS
    ) {
      setIsShowPopover(true);
    }
  }, [isEnabledForSender, isUnpaidUser, prevTransferState, transferState]);

  const onViewPopover = useCallback(() => {
    const viewCount = Number(
      Cookie.get(AppConfig.cookies.customWallpaperPopoverSeenCount.name) || 0
    );
    Cookie.set(
      AppConfig.cookies.customWallpaperPopoverSeenCount.name,
      (viewCount + 1).toString()
    );
  }, []);

  const onDismissPopover = useCallback((isForever?: boolean) => {
    setIsShowPopover(false);
    Cookie.set(AppConfig.cookies.customWallpaperPopoverClosed.name, 'true', {
      expires: getFutureDate(CLOSE_POPOVER_COOKIE_EXPIRY),
    });

    const viewCount = Number(
      Cookie.get(AppConfig.cookies.customWallpaperPopoverSeenCount.name) || 0
    );

    if (isForever || viewCount > MIN_POPOVER_VIEWS) {
      Cookie.set(
        AppConfig.cookies.customWallpaperPopoverDismissedForever.name,
        'true'
      );
    }
  }, []);

  const onAcceptPopover = useCallback(() => {
    setIsShowPopover(false);
    Cookie.set(AppConfig.cookies.customWallpaperPopoverClosed.name, 'true', {
      expires: getFutureDate(CLOSE_POPOVER_COOKIE_EXPIRY),
    });
    Cookie.set(
      AppConfig.cookies.customWallpaperPopoverDismissedForever.name,
      'true'
    );
  }, []);

  return {
    isShowPopover,
    onViewPopover,
    onDismissPopover,
    onAcceptPopover,
  };
};
